import { AppCollectionVisibility } from '@wix/wix-data-client-common-standalone'
import type { AppApi } from '@wix/wix-data-client-common'
import {
  isDbDriverCollection,
  isFormsCollection,
  getDeveloperMode,
} from '@wix/wix-data-client-common'
import type { SchemasClientAdapter } from './types'
import type { Logger, EditorSDK } from '@wix/wix-data-client-common-standalone'

export function decorateAdapterWithAppVisibilityControl({
  logger,
  adapter,
  editorSdkProxy,
  finalAppApiPromise,
}: {
  logger: Logger
  adapter: SchemasClientAdapter
  editorSdkProxy: EditorSDK
  finalAppApiPromise: Promise<AppApi>
}) {
  return { ...adapter, listSchemas }

  async function listSchemas({
    includeHiddenAppCollections,
    ...rest
  }: {
    includeHiddenAppCollections?: boolean
  } = {}) {
    const schemas = await adapter.listSchemas(rest)

    if (includeHiddenAppCollections || schemas.length === 0) {
      return schemas
    }

    const appApi = await finalAppApiPromise
    const visibility = await appApi
      .getAppCollectionVisibility()
      .catch(async (error: unknown) => {
        logger.error(
          new Error('Failed to get app collection visibility', {
            cause: error,
          }),
        )

        // An error when getting the visibility should not break the adapter.
        // Resort to the default based on the developer mode.
        const developerModeIsEnabled = await getDeveloperMode(editorSdkProxy)
        return developerModeIsEnabled
          ? AppCollectionVisibility.VISIBLE
          : AppCollectionVisibility.HIDDEN
      })

    if (visibility === AppCollectionVisibility.VISIBLE) {
      return schemas // Nothing to possibly filter out.
    }

    return schemas.filter(
      schema =>
        !isDbDriverCollection(schema) &&
        !(
          schema.storage === 'dynamicApplication' && !isFormsCollection(schema)
        ),
    )
  }
}
